'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export type Role = 'Admin' | 'Sales Manager' | 'Inventory Manager' | 'Merchandiser' | 'Supervisor';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  avatar: string;
  department: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  hasPermission: (module: string, action: 'view' | 'create' | 'edit' | 'delete' | 'approve') => boolean;
}

const MODULES = [
  'Executive Dashboard', 'Sales Dashboard', 'Inventory Dashboard',
  'Sales Orders', 'Quotations', 'Purchase Orders', 'Invoicing',
  'Product Catalog', 'Supplier Management', 'CRM / Customers',
  'Merchandiser', 'Driver / Deliveries', 'Sales Trucks', 'Reports', 'Settings',
];

type PermSet = { view: boolean; create: boolean; edit: boolean; delete: boolean; approve: boolean };

const ROLE_PERMISSIONS: Record<Role, Record<string, PermSet>> = {
  Admin: Object.fromEntries(MODULES.map(m => [m, { view: true, create: true, edit: true, delete: true, approve: true }])),
  'Sales Manager': Object.fromEntries(MODULES.map(m => [m, {
    view: true,
    create: ['Sales Orders', 'Quotations', 'Invoicing', 'CRM / Customers'].includes(m),
    edit: ['Sales Orders', 'Quotations', 'Invoicing', 'CRM / Customers'].includes(m),
    delete: false,
    approve: ['Sales Orders', 'Quotations'].includes(m),
  }])),
  'Inventory Manager': Object.fromEntries(MODULES.map(m => [m, {
    view: true,
    create: ['Purchase Orders', 'Product Catalog', 'Supplier Management'].includes(m),
    edit: ['Purchase Orders', 'Product Catalog', 'Supplier Management', 'Inventory Dashboard'].includes(m),
    delete: false,
    approve: ['Purchase Orders'].includes(m),
  }])),
  Merchandiser: Object.fromEntries(MODULES.map(m => [m, {
    view: ['Merchandiser', 'Product Catalog', 'CRM / Customers', 'Sales Dashboard'].includes(m),
    create: ['Merchandiser'].includes(m),
    edit: ['Merchandiser'].includes(m),
    delete: false,
    approve: false,
  }])),
  Supervisor: Object.fromEntries(MODULES.map(m => [m, {
    view: true,
    create: ['Sales Orders', 'Quotations', 'Merchandiser'].includes(m),
    edit: ['Sales Orders', 'Quotations', 'Merchandiser', 'CRM / Customers'].includes(m),
    delete: false,
    approve: ['Merchandiser'].includes(m),
  }])),
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const supabase = createClient();

  const buildAuthUser = useCallback(async (supabaseUser: any): Promise<AuthUser | null> => {
    if (!supabaseUser) return null;
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single();
      if (profile) {
        return {
          id: profile.id,
          name: profile.full_name || supabaseUser.email?.split('@')[0] || '',
          email: profile.email || supabaseUser.email || '',
          role: (profile.role as Role) || 'Sales Manager',
          avatar: profile.avatar || '',
          department: profile.department || '',
        };
      }
      // Fallback: build from auth metadata
      const meta = supabaseUser.user_metadata || {};
      return {
        id: supabaseUser.id,
        name: meta.full_name || supabaseUser.email?.split('@')[0] || '',
        email: supabaseUser.email || '',
        role: (meta.role as Role) || 'Sales Manager',
        avatar: meta.avatar || '',
        department: meta.department || '',
      };
    } catch {
      return null;
    }
  }, [supabase]);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      if (session?.user) {
        const authUser = await buildAuthUser(session.user);
        if (mounted) setUser(authUser);
      }
      if (mounted) setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      if (session?.user) {
        const authUser = await buildAuthUser(session.user);
        if (mounted) setUser(authUser);
      } else {
        if (mounted) setUser(null);
      }
      if (mounted) setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [buildAuthUser, supabase]);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { success: false, error: error.message || 'Invalid email or password.' };
      }
      if (data.user) {
        const authUser = await buildAuthUser(data.user);
        setUser(authUser);
      }
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e.message || 'Login failed.' };
    }
  }, [supabase, buildAuthUser]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, [supabase]);

  const hasPermission = useCallback((module: string, action: 'view' | 'create' | 'edit' | 'delete' | 'approve'): boolean => {
    if (!user) return false;
    return ROLE_PERMISSIONS[user.role]?.[module]?.[action] ?? false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { ROLE_PERMISSIONS };
